import { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, Zap, Coins } from "lucide-react";
import { useBoostSettings, durationChoices, formatBoostDate, formatCoco } from "@/hooks/useBoostSettings";

interface VideoBoostDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  videoId: string;
  /** Appelé après un boost réussi (rafraîchissement de la liste appelante). */
  onBoosted?: () => void;
}

interface Eligibility {
  active_count: number;
  max_active: number;
  cooldown_days: number;
  cooldown_until: string | null;
  has_active_boost: boolean;
  min_days: number;
  max_days: number;
  default_days: number;
}

/**
 * Achat d'un boost vidéo : montant de Coco engagé + durée en jours.
 * Le classement du feed utilise Coco/jour ; ce n'est pas un achat de vues.
 * Toutes les règles (plafond, carence, solde) sont validées par la RPC serveur.
 */
const VideoBoostDialog = ({ open, onOpenChange, videoId, onBoosted }: VideoBoostDialogProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { settings } = useBoostSettings();
  const [loading, setLoading] = useState(false);
  const [cocoBalance, setCocoBalance] = useState(0);
  const [cocoSpent, setCocoSpent] = useState("");
  const [days, setDays] = useState<number | null>(null);
  const [eligibility, setEligibility] = useState<Eligibility | null>(null);

  const load = useCallback(async () => {
    if (!user) return;

    const { data: balance } = await supabase
      .from("coco_points")
      .select("balance")
      .eq("user_id", user.id)
      .maybeSingle();
    if (balance) setCocoBalance(Number(balance.balance));

    const { data, error } = await supabase.rpc("get_video_boost_eligibility", { p_video_id: videoId });
    if (!error && data) setEligibility(data as unknown as Eligibility);
  }, [user, videoId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  useEffect(() => {
    if (days === null && settings) setDays(settings.defaultDays);
  }, [settings, days]);

  const amount = Number(cocoSpent);
  const amountValid = Number.isFinite(amount) && amount > 0;
  const cooldownBlocked = Boolean(eligibility?.cooldown_until);
  const quotaBlocked = Boolean(
    eligibility && !eligibility.has_active_boost && eligibility.active_count >= eligibility.max_active,
  );
  const alreadyBoosted = Boolean(eligibility?.has_active_boost);
  const blocked = cooldownBlocked || quotaBlocked || alreadyBoosted;

  const handleBoost = async () => {
    if (!user || !days || !amountValid) return;

    setLoading(true);
    try {
      const { error } = await supabase.rpc("create_video_boost", {
        p_video_id: videoId,
        p_coco: amount,
        p_days: days,
      });
      if (error) throw error;

      toast({
        title: "Boost activé",
        description: "Votre vidéo est mise en avant dans le feed.",
      });
      onOpenChange(false);
      onBoosted?.();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Boost impossible",
        description: error?.message || "Impossible d'activer le boost",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            Booster votre vidéo
          </DialogTitle>
          <DialogDescription>
            Le classement dépend des Coco engagés par jour. Le boost ne garantit pas de vues.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="flex items-center justify-between p-4 bg-secondary rounded-lg">
            <div className="flex items-center gap-2">
              <Coins className="h-5 w-5 text-primary" />
              <span className="font-semibold">Votre solde</span>
            </div>
            <span className="text-2xl font-bold">{cocoBalance.toFixed(0)} Coco</span>
          </div>

          {eligibility && (
            <p className="text-sm text-muted-foreground">
              Boosts en cours : {eligibility.active_count}/{eligibility.max_active}
            </p>
          )}

          <div className="space-y-2">
            <Label htmlFor="boost-coco">
              Coco en jeu <span className="text-destructive">*</span>
            </Label>
            <Input
              id="boost-coco"
              type="number"
              min={1}
              inputMode="numeric"
              value={cocoSpent}
              onChange={(e) => setCocoSpent(e.target.value)}
              placeholder="Ex. 300"
              disabled={blocked}
            />
          </div>

          <div className="space-y-2">
            <Label>
              Durée <span className="text-destructive">*</span>
            </Label>
            <div className="flex gap-2">
              {settings &&
                durationChoices(settings).map((d) => (
                  <Button
                    key={d}
                    type="button"
                    variant={days === d ? "default" : "outline"}
                    className="flex-1"
                    onClick={() => setDays(d)}
                    disabled={blocked}
                  >
                    {d} j
                  </Button>
                ))}
            </div>
          </div>

          {amountValid && days ? (
            <div className="p-4 border border-border rounded-lg flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Coco par jour/engagés</span>
              <span className="text-xl font-bold">
                ({formatCoco(amount / days)}/{formatCoco(amount)})
              </span>
            </div>
          ) : null}


          {alreadyBoosted && (
            <p className="text-sm text-destructive">Cette vidéo a déjà un boost en cours.</p>
          )}
          {quotaBlocked && eligibility && (
            <p className="text-sm text-destructive">
              Vous avez déjà {eligibility.max_active} vidéos en boost. Attendez la fin ou annulez-en une.
            </p>
          )}
          {cooldownBlocked && eligibility && (
            <p className="text-sm text-destructive">
              Vous pourrez rebooster cette vidéo le {formatBoostDate(eligibility.cooldown_until)}.
            </p>
          )}
          {amountValid && amount > cocoBalance && (
            <p className="text-sm text-destructive">
              Il vous manque {(amount - cocoBalance).toFixed(0)} Coco.
            </p>
          )}

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1" disabled={loading}>
              Annuler
            </Button>
            <Button
              onClick={handleBoost}
              className="flex-1"
              disabled={loading || blocked || !amountValid || !days || amount > cocoBalance}
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <Zap className="mr-2 h-4 w-4" />
              Booster
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VideoBoostDialog;
